// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState } from 'react';
import { Box, useTheme } from '@mui/system';
import { CSVLink } from 'react-csv';
import { Typography, Table, TableHead, TableRow, TableCell, TableBody, Button, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import downloadIcon from 'Assets/download-icon.png';
import { pricingDescription } from 'Utils/constants';
import { ADMIN_ROUTE } from 'Utils/routes';
import { LICENSE_FEE_PERCENT, licenseFeePercentText } from 'Utils/licenseFee';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { GetPricingThunk } from '../adminThunk';
import stylesDef from './styles';
import {
  ALL_ACCOUNTS_CSV_HEADERS, CONTRACT_RATE, LADDER_CSV_HEADERS, LEGACY_CSV_HEADERS,
  allAccountsFileName, allAccountsRows, annualSpendBand, contractRateFileName, discountLabel, ladderFileName, ladderRows,
  legacyAccountCount, legacyCellFileName, legacyCellRows, legacyRows, standingBreakdown,
} from './exports';

// The legacy band grid's plans: pay-as-you-go accounts stand in the ladder's PAYG row (AUT-6617 D2), so no_commit column
const LEGACY_PLANS = pricingDescription.filter((plan) => plan.name !== 'no_commit');

// The Credit Ladder page (AUT-6617): every account on a plan or subscription placed once at its ladder standing, PAYG
// as its own row; below it, labelled legacy, the AEC bands and the contract-rate accounts whose AEC orders still price
// from them. Read-only; the server's placement is the Subscription tab's resolver.
export const CreditLadder = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState(false);
  const { pricing, ladder } = useAppSelector((state) => state.admin);
  const { pricingAccess } = useAppSelector((state) => state.auth.user);

  useEffect(() => {
    const onLoad = async () => {
      await dispatch(GetPricingThunk());
      setLoaded(true);
    };
    if (pricingAccess) {
      onLoad();
    } else {
      navigate(ADMIN_ROUTE);
    }
  }, []);

  const bandTiers = pricing.filter((tier) => !tier.plans?.[CONTRACT_RATE]);
  const contractOrgs = pricing.find((tier) => tier.plans?.[CONTRACT_RATE])?.plans[CONTRACT_RATE]?.orgs || [];
  const legacyCount = ladder ? ladder.counts.legacy : legacyAccountCount(pricing);
  const showLegacy = legacyAccountCount(pricing) > 0;
  const ladderRowsToShow = ladder?.available ? [...ladder.tiers, ladder.payg] : [];

  // An older server sends no ladder: the all-accounts export falls back to the legacy rows it used to be
  const allAccounts = ladder
    ? { headers: ALL_ACCOUNTS_CSV_HEADERS, data: allAccountsRows(ladder, pricing) }
    : { headers: LEGACY_CSV_HEADERS, data: legacyRows(pricing) };

  const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;
  const summaryLine = () => {
    if (!ladder) return loaded ? 'Ladder standing not available.' : '';
    const counts = `${plural(ladder.counts.listed, 'account')} on a plan or subscription · ${ladder.counts.legacy} priced from legacy AEC bands or contract rates (below) · ${plural(ladder.counts.noPlan + ladder.counts.notBackfilled, 'organization')} with no plan ${ladder.counts.noPlan + ladder.counts.notBackfilled === 1 ? 'is' : 'are'} not listed`;
    return ladder.available ? counts : `The Credit Ladder has no tiers in this environment. ${counts}`;
  };

  // a plan row naming a ladder tier the environment no longer carries: the account is placed by its rate, and flagged
  const missingTierCount = (row: ILadderRow) => row.orgs.filter((org) => org.tierMissing).length;
  // AUT-6622 D10: the column above is the workbook's 10%; an account on its own percent prices its fee elsewhere, and
  // the tier's export names each one
  const nonStandardFeeCount = (row: ILadderRow) => row.orgs.filter((org) => org.licenseFeePercent !== LICENSE_FEE_PERCENT).length;

  return (
    <Box sx={styles.pageWrapper}>
      <Box sx={styles.header}>
        <Typography variant="h2">Credit Ladder</Typography>
        <CSVLink data={allAccounts.data} headers={allAccounts.headers} filename={allAccountsFileName()}>
          <Button variant="contained" color="primary">
            DOWNLOAD ALL ORG DETAILS
          </Button>
        </CSVLink>
      </Box>
      <Typography variant="body2" sx={styles.summary}>{summaryLine()}</Typography>
      <Divider sx={styles.divider} />
      {ladderRowsToShow.length > 0 && (
        <Table aria-label="credit ladder">
          <TableHead>
            <TableRow>
              <TableCell sx={styles.headCell}>tier</TableCell>
              <TableCell sx={styles.headCell}>annual spend</TableCell>
              <TableCell sx={styles.headCell} align="right">$/credit</TableCell>
              <TableCell sx={styles.headCell} align="right">$/credit incl. license fee</TableCell>
              <TableCell sx={styles.headCell} align="right">discount</TableCell>
              <TableCell sx={styles.headCell} align="right">accounts</TableCell>
              <TableCell sx={styles.exportCell} />
            </TableRow>
          </TableHead>
          <TableBody>
            {ladderRowsToShow.map((row) => (
              <TableRow key={row.name}>
                <TableCell sx={styles.cellText} component="th" scope="row">{row.displayName}</TableCell>
                <TableCell sx={styles.cellText}>{annualSpendBand(row)}</TableCell>
                <TableCell sx={styles.cellText} align="right">{`$${row.dollarPerCredit.toFixed(2)}`}</TableCell>
                <TableCell sx={styles.cellText} align="right">{`$${row.dollarPerCreditInclLicenseFee.toFixed(2)}`}</TableCell>
                <TableCell sx={styles.cellText} align="right">{discountLabel(row.discountPercent)}</TableCell>
                <TableCell sx={styles.cellText} align="right">
                  <Box sx={styles.accountsCell}>
                    <span>{row.orgs.length}</span>
                    {row.name !== 'PAYG' && row.orgs.length > 0 && (
                      <Box component="span" sx={styles.breakdown}>{standingBreakdown(row.orgs)}</Box>
                    )}
                    {missingTierCount(row) > 0 && (
                      <Box component="span" sx={styles.warning}>
                        {`${missingTierCount(row)} on a ladder tier this environment no longer carries (see the export)`}
                      </Box>
                    )}
                    {nonStandardFeeCount(row) > 0 && (
                      <Box component="span" sx={styles.breakdown}>
                        {`${nonStandardFeeCount(row)} on a license fee other than ${licenseFeePercentText(LICENSE_FEE_PERCENT)} (see the export)`}
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={styles.exportCell} align="right">
                  {row.orgs.length > 0 && (
                    <CSVLink data={ladderRows(row)} headers={LADDER_CSV_HEADERS} filename={ladderFileName(row)}>
                      <Box component="img" src={downloadIcon} alt={`Export ${row.displayName} accounts`} title={`Export ${row.displayName} accounts`} sx={styles.downloadIcon} />
                    </CSVLink>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {showLegacy && (
        <>
          <Box sx={styles.sectionHeader}>
            <Box>
              <Typography variant="h6">Legacy AEC pricing</Typography>
              <Typography variant="body2" sx={styles.sectionNote}>
                {`${plural(legacyCount, 'account')} whose AEC orders still price from a legacy band or contract rate — each also stands on the ladder above for catalog orders. This section empties as accounts are migrated (AUT-6571).`}
              </Typography>
            </Box>
          </Box>
          {bandTiers.length > 0 && (
            <Table aria-label="legacy pricing table">
              <TableHead>
                <TableRow>
                  <TableCell sx={styles.headCell}>lower limit</TableCell>
                  <TableCell sx={styles.headCell}>upper limit</TableCell>
                  {LEGACY_PLANS.map((plan) => (
                    <TableCell align="right" key={plan.name}>
                      <Box sx={styles.planNameContainer}>
                        <Box component="span" sx={styles.planName}>{plan.name.split('_').join(' ')}</Box>
                        <Box component="span" sx={[styles.planDescription, plan.name === 'enterprise_pre_commit' ? styles.grey : styles.green]}>
                          {plan.description}
                        </Box>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {bandTiers.map((tier) => (
                  <TableRow key={tier.upperLimit}>
                    <TableCell sx={styles.cellText} component="th" scope="row">
                      {tier.lowerLimit}
                    </TableCell>
                    <TableCell sx={styles.cellText} component="th" scope="row">
                      {tier.upperLimit}
                    </TableCell>
                    {LEGACY_PLANS.map((plan) => {
                      const cell = tier.plans[plan.name];
                      return (
                        <TableCell sx={styles.cellText} align="right" key={plan.name}>
                          <Box sx={styles.priceContainer}>
                            {cell ? `$${cell.rate?.toFixed(2)}` : '-'}
                            {(cell?.orgs?.length ?? 0) > 0 && (
                              <CSVLink data={legacyCellRows(plan.name, cell, tier)} headers={LEGACY_CSV_HEADERS} filename={legacyCellFileName(plan.name, tier.upperLimit)}>
                                <Box component="img" src={downloadIcon} alt="Export Customers List" title="Export Customers List" sx={styles.downloadIcon} />
                              </CSVLink>
                            )}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {contractOrgs.length > 0 && (
            <Box sx={styles.header}>
              <Box>
                <Typography variant="h6">Contract rate</Typography>
                <Typography variant="body2">
                  {`${plural(contractOrgs.length, 'subscription account')} at their own price per credit — no credit band`}
                </Typography>
              </Box>
              <CSVLink
                data={legacyCellRows(CONTRACT_RATE, { rate: null, orgs: contractOrgs }, { lowerLimit: null, upperLimit: null, plans: {} })}
                headers={LEGACY_CSV_HEADERS}
                filename={contractRateFileName()}
              >
                <Box component="img" src={downloadIcon} alt="Export Contract Rate Accounts" title="Export Contract Rate Accounts" sx={styles.downloadIcon} />
              </CSVLink>
            </Box>
          )}
        </>
      )}
      <Box sx={styles.end} />
    </Box>
  );
};

export default CreditLadder;
